/**
 * On business report sites all years live in a single site, distinguished by slug
 * (e.g. /geschaeftsbericht-2025). There is no entry at the site root, so the root
 * (`/` for br_de, `/br_fr`, `/br_en`) must redirect to the newest report.
 *
 * This is resolved dynamically from the CMS (the year-root at the top of the tree)
 * so editors can publish a new year without a developer touching redirects.
 */
export default defineNuxtRouteMiddleware(async (to) => {
  // `useI18n()` requires a setup context, so read the locale state from the injected composer
  const { $i18n } = useNuxtApp()
  const locale = $i18n.locale.value
  const locales = $i18n.locales.value
  const localeProperties = $i18n.localeProperties.value

  // Only relevant on the business report sites
  if (!localeProperties?.isBusinessReport) return

  // Strip the locale prefix to get the uri within the site
  const localeRegex = new RegExp(
    `^/(${locales.map(l => (typeof l === 'string' ? l : l.code)).join('|')})(?=/|$)`,
  )
  const uri = to.path.replace(localeRegex, '').replace(/\/$/, '') || '/'

  // Only the bare site root needs redirecting
  if (uri !== '/') return

  const GqlInstance = useGql()
  const { entries } = await GqlInstance('BusinessReports', { site: locale })

  const reports = (entries?.data ?? []) as Array<{
    url?: string | null
    year?: { value?: string | null }
  }>

  // Newest report = highest year (the top of the collection tree)
  const newest = [...reports].sort(
    (a, b) => Number(b?.year?.value ?? 0) - Number(a?.year?.value ?? 0),
  )[0]

  if (newest?.url && newest.url !== to.path) {
    return navigateTo(newest.url, { redirectCode: 302 })
  }
})
